import { InfoCircleIcon } from '@langchain/untitled-ui-icons';
import { Button, LinearProgress } from '@mui/joy';

import { useEffect } from 'react';
import {
  Link,
  Outlet,
  useMatches,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { Error } from '@/components/Hub/Error';
import HubSearchBox from '@/components/Hub/HubSearchBox';
import { Tooltip } from '@/design-system';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { useOrganizationId, useSelectedTenant } from '@/hooks/useSwr';

import Breadcrumbs from '../../components/Breadcrumbs';

const LoginButton = () => {
  const navigate = useNavigate();
  return (
    <div className="grow-0 place-content-end">
      <Button variant="outlined" onClick={() => navigate(`/`)} size="sm">
        Log in
      </Button>
    </div>
  );
};

export function HubHeader({ children }: { children?: React.ReactNode }) {
  return (
    <div className="px-4 py-3">
      <Breadcrumbs />
      <div className="flex flex-1 items-center justify-end gap-2">
        {children}
      </div>
    </div>
  );
}

export function HubLayout() {
  const { authed, loading: authLoading } = useAuth();
  const matches = useMatches();
  const match = matches[matches.length - 1];
  const hideSearch =
    typeof match.handle === 'object' &&
    match.handle != null &&
    'hideSearch' in match.handle &&
    match.handle.hideSearch === true;

  const organizationId = useOrganizationId();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: selectedTenant,
    isLoading: tenantLoading,
    error: tenantError,
  } = useSelectedTenant();

  useEffect(() => {
    if (selectedTenant?.id && !organizationId) {
      searchParams.append('organizationId', selectedTenant.id);
      setSearchParams(searchParams);
    }
  }, [organizationId, selectedTenant?.id]);

  const error = authed && tenantError;
  if (error) {
    return (
      <div className="flex min-h-screen flex-col">
        <HubHeader />
        <Error>{error.message}</Error>
      </div>
    );
  }
  const loading = authLoading || tenantLoading;

  if (loading) {
    return (
      <div className="min-h-screen">
        <HubHeader />
        <LinearProgress />
      </div>
    );
  }

  const hasHandle = selectedTenant?.tenant_handle;

  /*
  What are the states we care about?
  - Full access (show my repos etc)
  - No handle set
  - Public
  */

  const state: 'no-access' | 'no-tenant' | 'needs-handle' | 'full-access' =
    (() => {
      if (!authed) return 'no-access';
      if (!selectedTenant) return 'no-tenant';
      if (!hasHandle) return 'needs-handle';
      return 'full-access';
    })();

  return (
    <div className="flex min-h-screen flex-col">
      <HubHeader>
        {!hideSearch && (
          <div className="mr-auto max-w-md grow">
            <HubSearchBox />
          </div>
        )}
        {state === 'no-tenant' && (
          <div className="grow-0 place-content-end">
            <Link to={`/`}>
              <div className="inline-flex h-5 items-center justify-start gap-[4px] rounded-2xl">
                <div>
                  <div className="h-[6px] w-[6px] rounded-[33px] bg-red-600" />
                </div>
                <Tooltip title="You have selected an unavailable organization. Please select one in the bottom-left corner.">
                  <div className="whitespace-nowrap text-center text-sm font-normal leading-tight text-error">
                    Select Organization <InfoCircleIcon className="size-4" />
                  </div>
                </Tooltip>
              </div>
            </Link>
          </div>
        )}
        {state === 'no-access' && <LoginButton />}
      </HubHeader>
      <Outlet />
    </div>
  );
}
